import { isValid } from "date-fns";
import { enums } from "../../enums/enums";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { Mercancia } from "../../entities/solicitudes/Mercancia";
import { MercanciaDatosComplementarios } from "../../entities/solicitudes/MercanciaDatosComplementarios";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IDocumento } from "../../interfaces/IDocumento";
import {
    buildGuiaRecalcularTarifaRequest,
    GuiaRecalcularTarifaRequest
} from "./buildGuiaRecalcularTarifaRequest";
import { consolidarServiciosGuia } from "./consolidarServiciosGuiaHelpers";
import { buildGuiaDireccionSnapshot } from "./buildGuiaDireccionSnapshot";

export type GuiaCreateRequest = GuiaRecalcularTarifaRequest & {
    operationId: string;
    observaciones?: string;
    descripcionMercancia?: string;
    clienteReferencias: Array<{
        guiaClienteReferenciaId: number;
        clienteReferenciaId: number;
        descripcion: string;
        referencia: string;
    }>;
    complementarios: Array<{
        guiaDatoComplementarioId: number;
        cantidad: number;
        claveUnidadId: number;
        claveProductoId: number;
        descripcion: string;
        fraccionArancelariaId: number;
        uuidComprobante: string;
        peso: number;
    }>;
    documentosRetorno: Array<{
        guiaRetornoDocumentoId: number;
        clienteDocumentoId: number;
        descripcion: string;
    }>;
    fechaCitaRad?: string | null;
    fechaFinRad?: string | null;
    horaRadFHDe?: string;
    horaRadFHHasta?: string;
    esRadFueraHorario: number;
    esOcurreOrigen: number;
    fechaCitaEad?: string | null;
    fechaFinEad?: string | null;
    esEadFueraHorario: number;
    horaEadFHDe?: string;
    horaEadFHHasta?: string;
    esOcurreDestino: number;
    fechaCitaRadCedis?: string | null;
    fechaFinRadCedis?: string | null;
    fechaCitaEadCedis?: string | null;
    fechaFinEadCedis?: string | null;
    telefonoAdicionalDestinatario?: string;
    direccionRemitente?: string;
    direccionDestinatario?: string;
    sucursalClienteId: number;
    areaServicioClienteId: number;
};

type BuildArgs = {
    operationId: string;
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    guiaMercancias: Mercancia[];
    guiaMercanciasComplementarias: MercanciaDatosComplementarios[];
    guiaServicios: ServicioAdicional[];
    idioma?: string;
};

const asInt = (value: unknown, fallback = 0): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const toIsoOrNull = (fecha?: Date): string | null => {
    if (!fecha || !isValid(fecha)) {
        return null;
    }
    return fecha.toISOString();
};

const mapFechasModalidad = (rec: IRecoleccion, esOrigen: boolean) => {
    const modalidad = esOrigen ? rec.tipoDeRecoleccionOrigen : rec.tipoDeRecoleccionDestino;
    const esFueraHorario = !!(modalidad === 3 && rec.EsFueraHorario);
    const tieneCita = modalidad === 2 || modalidad === 3;

    return {
        fechaCita: tieneCita ? toIsoOrNull(rec.fechaCita) : null,
        fechaFin: modalidad === 2 && rec.fechaCita ? toIsoOrNull(rec.fechaCita) : null,
        esFueraHorario: esFueraHorario ? 1 : 0,
        horaDe: esFueraHorario ? (rec.horaFHDe ?? "") : "",
        horaHasta: esFueraHorario ? (rec.horaFHHasta ?? "") : "",
        fechaCitaCedis: toIsoOrNull(rec.fechaCitaCedis),
        fechaFinCedis: toIsoOrNull(rec.fechaCitaCedis)
    };
};

const mapComplementarios = (items: MercanciaDatosComplementarios[]) =>
    (items ?? []).map((c, index) => ({
        guiaDatoComplementarioId: index + 1,
        cantidad: asInt(c.cantidad),
        claveUnidadId: asInt(c.claveUnidadId),
        claveProductoId: asInt(c.claveProductoId),
        descripcion: c.descripcionMercancia?.trim() ?? "",
        fraccionArancelariaId: asInt(c.claveFraccionArancelariaId),
        uuidComprobante: c.uuidComprobante?.trim() ?? "",
        peso: Number(c.peso) || 0
    }));

const mapReferencias = (guiaWizard: IGuiaWizard) =>
    (guiaWizard.clienteReferencias ?? [])
        .filter(r => r.valor?.trim())
        .map((r, index) => ({
            guiaClienteReferenciaId: index + 1,
            clienteReferenciaId: r.clienteReferenciaID,
            descripcion: r.descripcion ?? "",
            referencia: r.valor?.trim() ?? ""
        }));

const mapDocumentosRetorno = (documentos: IDocumento[]) =>
    (documentos ?? []).map((d, index) => ({
        guiaRetornoDocumentoId: index + 1,
        clienteDocumentoId: asInt(d.clienteDocumentoID),
        descripcion: d.documento?.trim() ?? ""
    }));

const telefonoDestinatario = (rec: IRecoleccion): string => {
    const contacto = rec.contacto as { telefono?: string; telefonoAdicional?: string } | undefined;
    return contacto?.telefonoAdicional?.trim()
        || contacto?.telefono?.trim()
        || "";
};

export function buildCreateGuiaRequest(args: BuildArgs): GuiaCreateRequest {
    const {
        operationId,
        guiaWizard,
        guiaOrigen,
        guiaDestino,
        guiaMercancias,
        guiaMercanciasComplementarias,
        guiaServicios
    } = args;

    const base = buildGuiaRecalcularTarifaRequest({
        guiaWizard,
        guiaOrigen,
        guiaDestino,
        guiaMercancias,
        guiaServicios,
        idioma: args.idioma
    });

    const consolidado = consolidarServiciosGuia(
        guiaOrigen?.serviciosAdicionales ?? [],
        guiaDestino?.serviciosAdicionales ?? [],
        guiaServicios ?? []
    );

    const origenFechas = mapFechasModalidad(guiaOrigen, true);
    const destinoFechas = mapFechasModalidad(guiaDestino, false);

    const esOcurreOrigen = guiaOrigen?.tipoRecoleccionId === enums.tipoRecoleccion.ocurre ? 1 : 0;
    const esOcurreDestino = guiaWizard.esOcurreDestino
        || guiaDestino?.tipoRecoleccionId === enums.tipoRecoleccion.ocurre
        ? 1
        : 0;

    return {
        ...base,
        operationId,
        observaciones: guiaWizard.observaciones?.trim() ?? "",
        descripcionMercancia: guiaWizard.descripcionMercancia?.trim() ?? "",
        clienteReferencias: mapReferencias(guiaWizard),
        complementarios: mapComplementarios(guiaMercanciasComplementarias),
        documentosRetorno: mapDocumentosRetorno(consolidado.documentosRetorno),
        fechaCitaRad: origenFechas.fechaCita,
        fechaFinRad: origenFechas.fechaFin,
        horaRadFHDe: origenFechas.horaDe,
        horaRadFHHasta: origenFechas.horaHasta,
        esRadFueraHorario: origenFechas.esFueraHorario,
        esOcurreOrigen,
        fechaCitaEad: destinoFechas.fechaCita,
        fechaFinEad: destinoFechas.fechaFin,
        esEadFueraHorario: destinoFechas.esFueraHorario,
        horaEadFHDe: destinoFechas.horaDe,
        horaEadFHHasta: destinoFechas.horaHasta,
        esOcurreDestino,
        fechaCitaRadCedis: origenFechas.fechaCitaCedis,
        fechaFinRadCedis: origenFechas.fechaFinCedis,
        fechaCitaEadCedis: destinoFechas.fechaCitaCedis,
        fechaFinEadCedis: destinoFechas.fechaFinCedis,
        telefonoAdicionalDestinatario: telefonoDestinatario(guiaDestino),
        direccionRemitente: buildGuiaDireccionSnapshot(guiaOrigen),
        direccionDestinatario: buildGuiaDireccionSnapshot(guiaDestino),
        sucursalClienteId: 0,
        areaServicioClienteId: 0
    };
}
